import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Loader2, AlertCircle, RefreshCw, Edit2, Power, PowerOff, Save, X, Plus, Trash2, BarChart3, Calendar, Clock, Play, Square, Download, CheckCircle, History } from 'lucide-react';
import { DataSourceControllerApi, DataSource, DataSourceCreateRequest, DataSourceUpdateRequest, ImportSession, ImportSessionStatusEnum, PageImportSession, ImportConfigRequest } from '../api';
import { apiConfiguration } from "../api/apiConfig";
import BbxCoverage from './BbxCoverage';

interface DataSourcesProps {
    refreshKey?: number;
    onRefresh?: () => void;
    onImportComplete?: () => void;
}

// Renders an import-stat cell: the Algolia-crawl +new / ~modified / -deleted on top, and — only when
// the gap-fill pass touched this entity — a separate "gap" block below so the two sources never blur.
const EntityCountCell: React.FC<{
    crawlNew: number;
    crawlMod: number;
    crawlDel?: number;
    gapNew: number;
    gapMod: number;
    gapDel?: number;
}> = ({ crawlNew, crawlMod, crawlDel, gapNew, gapMod, gapDel }) => {
    const hasGap = gapNew > 0 || gapMod > 0 || (gapDel || 0) > 0;
    return (
        <div style={{ fontSize: '11px', lineHeight: '1.3' }}>
            <div style={{ color: 'var(--success)' }}>+{crawlNew}</div>
            <div style={{ color: 'var(--warning)' }}>~{crawlMod}</div>
            {crawlDel !== undefined && <div style={{ color: 'var(--danger)' }}>-{crawlDel}</div>}
            {hasGap && (
                <div
                    style={{ color: 'var(--info)', marginTop: '3px', paddingTop: '3px', borderTop: '1px dashed var(--border)' }}
                    title="gap-fill changes (wines prod_product can't enumerate, re-verified each run)"
                >
                    <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.8 }}>gap</div>
                    <div>+{gapNew}</div>
                    <div>~{gapMod}</div>
                    {gapDel !== undefined && gapDel > 0 && <div>-{gapDel}</div>}
                </div>
            )}
        </div>
    );
};

const DataSources: React.FC<DataSourcesProps> = ({ refreshKey, onRefresh, onImportComplete }) => {
    const [dataSources, setDataSources] = useState<DataSource[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editForm, setEditForm] = useState<DataSourceUpdateRequest>({});
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createForm, setCreateForm] = useState<DataSourceCreateRequest>({});
    const [actionLoading, setActionLoading] = useState<number | null>(null);

    // Import-related state
    const [expandedImports, setExpandedImports] = useState<Set<number>>(new Set());
    const [importSessions, setImportSessions] = useState<{ [key: string]: ImportSession[] }>({});
    const [runningImports, setRunningImports] = useState<Set<number>>(new Set());
    const [includeProductDetails, setIncludeProductDetails] = useState<boolean>(true); // Default to ON (smart enrichment only for new/changed vintages)

    // Use ref to avoid recreating API instance
    const apiRef = useRef(new DataSourceControllerApi(apiConfiguration));

    const styles = {
        container: {
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '24px',
            backgroundColor: 'var(--canvas)',
            minHeight: '100vh',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px',
        },
        title: {
            fontSize: '32px',
            fontWeight: '700' as const,
            color: 'var(--ink-900)',
            margin: 0,
        },
        headerActions: {
            display: 'flex',
            gap: '12px',
        },
        button: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: 'var(--claret-600)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer' as const,
            fontSize: '14px',
            fontWeight: '500' as const,
            transition: 'all 0.2s ease',
        },
        buttonSmall: {
            padding: '6px 12px',
            fontSize: '12px',
        },
        buttonSecondary: {
            backgroundColor: 'var(--ink-500)',
        },
        buttonDanger: {
            backgroundColor: 'var(--danger)',
        },
        buttonSuccess: {
            backgroundColor: 'var(--success)',
        },
        card: {
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            marginBottom: '16px',
            overflow: 'hidden' as const,
            transition: 'all 0.2s ease',
        },
        cardHeader: {
            padding: '24px',
            borderBottom: '1px solid var(--border)',
        },
        cardContent: {
            padding: '24px',
        },
        cardTitle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
        },
        cardName: {
            fontSize: '20px',
            fontWeight: '600' as const,
            color: 'var(--ink-900)',
            margin: 0,
        },
        cardActions: {
            display: 'flex',
            gap: '8px',
        },
        statusBadge: (enabled: boolean) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '500' as const,
            backgroundColor: enabled ? 'var(--success-bg)' : 'var(--danger-bg)',
            color: enabled ? 'var(--success)' : 'var(--danger)',
        }),
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
        },
        statCard: {
            padding: '16px',
            backgroundColor: 'var(--hover)',
            borderRadius: '8px',
            border: '1px solid var(--border)',
        },
        statLabel: {
            fontSize: '12px',
            color: 'var(--ink-500)',
            fontWeight: '500' as const,
            textTransform: 'uppercase' as const,
            marginBottom: '4px',
        },
        statValue: {
            fontSize: '18px',
            fontWeight: '600' as const,
            color: 'var(--ink-900)',
        },
        form: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '16px',
        },
        formGroup: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '6px',
        },
        label: {
            fontSize: '14px',
            fontWeight: '500' as const,
            color: 'var(--ink-700)',
        },
        input: {
            padding: '10px 12px',
            border: '1px solid var(--border-strong)',
            borderRadius: '6px',
            fontSize: '14px',
            color: 'var(--ink-900)',
        },
        textarea: {
            padding: '10px 12px',
            border: '1px solid var(--border-strong)',
            borderRadius: '6px',
            fontSize: '14px',
            color: 'var(--ink-900)',
            minHeight: '80px',
            resize: 'vertical' as const,
        },
        checkbox: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        },
        formActions: {
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end',
        },
        loadingContainer: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            color: 'var(--ink-500)',
        },
        errorContainer: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            backgroundColor: 'var(--danger-bg)',
            border: '1px solid var(--danger-border)',
            borderRadius: '12px',
            padding: '32px',
        },
        createFormContainer: {
            backgroundColor: 'var(--hover)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
        },
        importSection: {
            borderTop: '1px solid var(--border)',
            paddingTop: '24px',
            marginTop: '24px',
        },
        importActions: {
            display: 'flex',
            gap: '8px',
            marginBottom: '16px',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse' as const,
            fontSize: '12px',
            marginTop: '16px',
        },
        th: {
            textAlign: 'left' as const,
            padding: '8px 12px',
            fontWeight: '600' as const,
            color: 'var(--ink-700)',
            borderBottom: '2px solid var(--border)',
            backgroundColor: 'var(--hover)',
        },
        td: {
            padding: '8px 12px',
            borderBottom: '1px solid var(--border)',
            verticalAlign: 'middle' as const,
        },
    };

    const loadDataSources = async () => {
        try {
            setLoading(true);
            setError(null);
            const { data } = await apiRef.current.getAllDataSources();
            setDataSources(data);
        } catch (err) {
            setError('Failed to load data sources');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadImportSessions = async (dataSourceCode: string) => {
        try {
            const { data } = await apiRef.current.getImportSessions(dataSourceCode, 0, 10, 'startTime', 'desc');
            setImportSessions(prev => ({
                ...prev,
                [dataSourceCode]: data.content || []
            }));
        } catch (err) {
            console.error('Failed to load import sessions:', err);
        }
    };

    const checkRunningImports = useCallback(async () => {
        try {
            const { data } = await apiRef.current.getAllRunningImports();
            const runningIds = new Set(data.map(session => session.dataSource?.id).filter(Boolean) as number[]);
            setRunningImports(runningIds);
        } catch (err) {
            console.error('Failed to check running imports:', err);
        }
    }, []); // Remove api dependency

    const startImport = async (dataSource: DataSource) => {
        try {
            setActionLoading(dataSource.id!);
            const config: ImportConfigRequest = {
                includeProductDetails: includeProductDetails
            };
            await apiRef.current.startImport(dataSource.code!, config);
            await loadImportSessions(dataSource.code!);
            checkRunningImports();
            if (onImportComplete) onImportComplete();
        } catch (err) {
            setError('Failed to start import');
            console.error(err);
        } finally {
            setActionLoading(null);
        }
    };

    const stopImport = async (dataSource: DataSource) => {
        try {
            setActionLoading(dataSource.id!);
            // Get the running session for this data source
            const { data } = await apiRef.current.getRunningImportSession(dataSource.code!);
            if (data.id) {
                await apiRef.current.stopImportSession(dataSource.code!, data.id);
                await loadImportSessions(dataSource.code!);
                checkRunningImports();
                if (onImportComplete) onImportComplete();
            }
        } catch (err) {
            setError('Failed to stop import');
            console.error(err);
        } finally {
            setActionLoading(null);
        }
    };

    const deleteImportSession = async (sessionId: number, dataSourceCode: string) => {
        try {
            await apiRef.current.deleteSession(sessionId);
            await loadImportSessions(dataSourceCode);
        } catch (err) {
            setError('Failed to delete import session');
            console.error(err);
        }
    };

    const toggleImportHistory = (dataSourceId: number, dataSourceCode: string) => {
        const newExpanded = new Set(expandedImports);
        if (expandedImports.has(dataSourceId)) {
            newExpanded.delete(dataSourceId);
        } else {
            newExpanded.add(dataSourceId);
            // Load import sessions when expanding
            loadImportSessions(dataSourceCode);
        }
        setExpandedImports(newExpanded);
    };

    useEffect(() => {
        loadDataSources();
        checkRunningImports();
    }, [refreshKey]); // Remove checkRunningImports from dependencies

    // Poll for running imports with separate effect
    useEffect(() => {
        const interval = setInterval(checkRunningImports, 5000);
        return () => clearInterval(interval);
    }, []); // Empty dependency array

    const handleEdit = (dataSource: DataSource) => {
        setEditingId(dataSource.id!);
        setEditForm({
            name: dataSource.name,
            description: dataSource.description,
            apiUrl: dataSource.apiUrl,
            apiKey: dataSource.apiKey,
            pollIntervalMinutes: dataSource.pollIntervalMinutes,
            enabled: dataSource.enabled,
            autoImportEnabled: dataSource.autoImportEnabled,
        });
    };

    const handleSaveEdit = async (id: number) => {
        try {
            setActionLoading(id);
            const dataSource = dataSources.find(ds => ds.id === id);
            if (!dataSource?.code) {
                throw new Error('Data source code not found');
            }
            await apiRef.current.updateDataSource(dataSource.code, editForm);
            setEditingId(null);
            setEditForm({});
            await loadDataSources();
            if (onRefresh) onRefresh();
        } catch (err) {
            setError('Failed to update data source');
            console.error(err);
        } finally {
            setActionLoading(null);
        }
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditForm({});
    };

    const handleToggleEnabled = async (dataSource: DataSource) => {
        try {
            setActionLoading(dataSource.id!);
            await apiRef.current.toggleDataSource(dataSource.code!);
            await loadDataSources();
            if (onRefresh) onRefresh();
        } catch (err) {
            setError('Failed to toggle data source');
            console.error(err);
        } finally {
            setActionLoading(null);
        }
    };

    const handleResetStats = async (dataSource: DataSource) => {
        try {
            setActionLoading(dataSource.id!);
            await apiRef.current.resetStatistics(dataSource.code!);
            await loadDataSources();
            if (onRefresh) onRefresh();
        } catch (err) {
            setError('Failed to reset statistics');
            console.error(err);
        } finally {
            setActionLoading(null);
        }
    };

    const handleDelete = async (dataSource: DataSource) => {
        if (!window.confirm(`Are you sure you want to delete data source "${dataSource.name}"?`)) {
            return;
        }

        try {
            setActionLoading(dataSource.id!);
            await apiRef.current.deleteDataSource(dataSource.code!);
            await loadDataSources();
            if (onRefresh) onRefresh();
        } catch (err) {
            setError('Failed to delete data source');
            console.error(err);
        } finally {
            setActionLoading(null);
        }
    };

    const handleCreate = async () => {
        try {
            setActionLoading(-1);
            await apiRef.current.createDataSource(createForm);
            setShowCreateForm(false);
            setCreateForm({});
            await loadDataSources();
            if (onRefresh) onRefresh();
        } catch (err) {
            setError('Failed to create data source');
            console.error(err);
        } finally {
            setActionLoading(null);
        }
    };

    const formatDate = (dateStr: string | undefined) => {
        if (!dateStr) return 'Never';
        try {
            return new Date(dateStr).toLocaleString('en-GB');
        } catch {
            return 'Invalid date';
        }
    };

    const getStatusIcon = (status?: ImportSessionStatusEnum) => {
        switch (status) {
            case ImportSessionStatusEnum.Running:
                return <RefreshCw size={14} style={{ color: 'var(--claret-600)' }} className="animate-spin" />;
            case ImportSessionStatusEnum.Completed:
                return <CheckCircle size={14} style={{ color: 'var(--success)' }} />;
            case ImportSessionStatusEnum.Failed:
                return <AlertCircle size={14} style={{ color: 'var(--danger)' }} />;
            default:
                return <Clock size={14} style={{ color: 'var(--ink-500)' }} />;
        }
    };

    const getDuration = (startTime?: string, endTime?: string) => {
        if (!startTime) return null;
        try {
            const start = new Date(startTime);
            const end = endTime ? new Date(endTime) : new Date();
            const durationMs = end.getTime() - start.getTime();
            const minutes = Math.floor(durationMs / 60000);
            const seconds = Math.floor((durationMs % 60000) / 1000);
            if (minutes > 0) {
                return `${minutes}m ${seconds}s`;
            }
            return `${seconds}s`;
        } catch {
            return null;
        }
    };

    if (loading && !dataSources.length) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <Loader2 size={32} className="animate-spin" color="var(--ink-900)" />
                    <p style={{marginTop: '16px', fontSize: '16px'}}>Loading data sources...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.container}>
                <div style={styles.errorContainer}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px'}}>
                        <AlertCircle size={20} color="var(--danger)" />
                        <span style={{color: 'var(--danger)', fontSize: '16px', fontWeight: '500'}}>{error}</span>
                    </div>
                    <button
                        style={{...styles.button, ...styles.buttonSecondary}}
                        onClick={() => {
                            setError(null);
                            loadDataSources();
                        }}
                    >
                        <RefreshCw size={16} />
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Data Sources & Imports</h1>
                <div style={styles.headerActions}>
                    <button
                        style={{...styles.button, ...styles.buttonSecondary}}
                        onClick={loadDataSources}
                    >
                        <RefreshCw size={16} />
                        Refresh
                    </button>
                    <button
                        style={styles.button}
                        onClick={() => setShowCreateForm(true)}
                    >
                        <Plus size={16} />
                        Add Data Source
                    </button>
                </div>
            </div>

            <BbxCoverage />

            {showCreateForm && (
                <div style={styles.createFormContainer}>
                    <h3 style={{marginTop: 0, marginBottom: '16px'}}>Create New Data Source</h3>
                    <div style={styles.form}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Code</label>
                            <input
                                style={styles.input}
                                value={createForm.code || ''}
                                onChange={(e) => setCreateForm(prev => ({...prev, code: e.target.value}))}
                                placeholder="Unique identifier"
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Name</label>
                            <input
                                style={styles.input}
                                value={createForm.name || ''}
                                onChange={(e) => setCreateForm(prev => ({...prev, name: e.target.value}))}
                                placeholder="Display name"
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Description</label>
                            <textarea
                                style={styles.textarea}
                                value={createForm.description || ''}
                                onChange={(e) => setCreateForm(prev => ({...prev, description: e.target.value}))}
                                placeholder="Description of the data source"
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>API URL</label>
                            <input
                                style={styles.input}
                                value={createForm.apiUrl || ''}
                                onChange={(e) => setCreateForm(prev => ({...prev, apiUrl: e.target.value}))}
                                placeholder="https://api.example.com"
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>API Key</label>
                            <input
                                type="password"
                                style={styles.input}
                                value={createForm.apiKey || ''}
                                onChange={(e) => setCreateForm(prev => ({...prev, apiKey: e.target.value}))}
                                placeholder="API authentication key"
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Poll Interval (minutes)</label>
                            <input
                                type="number"
                                style={styles.input}
                                value={createForm.pollIntervalMinutes || ''}
                                onChange={(e) => setCreateForm(prev => ({...prev, pollIntervalMinutes: parseInt(e.target.value) || undefined}))}
                                placeholder="60"
                            />
                        </div>
                        <div style={styles.formActions}>
                            <button
                                style={{...styles.button, ...styles.buttonSecondary}}
                                onClick={() => {
                                    setShowCreateForm(false);
                                    setCreateForm({});
                                }}
                            >
                                <X size={16} />
                                Cancel
                            </button>
                            <button
                                style={styles.button}
                                onClick={handleCreate}
                                disabled={actionLoading === -1}
                            >
                                {actionLoading === -1 ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                Create
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {dataSources.map((dataSource) => {
                const isRunning = runningImports.has(dataSource.id!);
                const isExpanded = expandedImports.has(dataSource.id!);
                const sessions = importSessions[dataSource.code!] || [];

                return (
                    <div key={dataSource.id} style={styles.card}>
                        <div style={styles.cardHeader}>
                            <div style={styles.cardTitle}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                    <h3 style={styles.cardName}>{dataSource.name}</h3>
                                    <span style={styles.statusBadge(dataSource.enabled || false)}>
                                        {dataSource.enabled ? <Power size={12} /> : <PowerOff size={12} />}
                                        {dataSource.enabled ? 'Enabled' : 'Disabled'}
                                    </span>
                                    {isRunning && (
                                        <span style={{...styles.statusBadge(true), backgroundColor: 'var(--info-bg)', color: 'var(--info)'}}>
                                            <RefreshCw size={12} className="animate-spin" />
                                            Importing
                                        </span>
                                    )}
                                </div>
                                <div style={styles.cardActions}>
                                    <button
                                        style={{...styles.button, ...styles.buttonSecondary, padding: '8px'}}
                                        onClick={() => handleResetStats(dataSource)}
                                        disabled={actionLoading === dataSource.id}
                                        title="Reset Statistics"
                                    >
                                        {actionLoading === dataSource.id ? <Loader2 size={16} className="animate-spin" /> : <BarChart3 size={16} />}
                                    </button>
                                    <button
                                        style={{...styles.button, ...(dataSource.enabled ? styles.buttonDanger : styles.buttonSuccess), padding: '8px'}}
                                        onClick={() => handleToggleEnabled(dataSource)}
                                        disabled={actionLoading === dataSource.id}
                                        title={dataSource.enabled ? 'Disable' : 'Enable'}
                                    >
                                        {actionLoading === dataSource.id ? <Loader2 size={16} className="animate-spin" /> : (dataSource.enabled ? <PowerOff size={16} /> : <Power size={16} />)}
                                    </button>
                                    <button
                                        style={{...styles.button, ...styles.buttonSecondary, padding: '8px'}}
                                        onClick={() => handleEdit(dataSource)}
                                        disabled={editingId === dataSource.id}
                                        title="Edit"
                                    >
                                        <Edit2 size={16} />
                                    </button>
                                    <button
                                        style={{...styles.button, ...styles.buttonDanger, padding: '8px'}}
                                        onClick={() => handleDelete(dataSource)}
                                        disabled={actionLoading === dataSource.id}
                                        title="Delete"
                                    >
                                        {actionLoading === dataSource.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                                    </button>
                                </div>
                            </div>

                            <div style={{fontSize: '14px', color: 'var(--ink-500)', marginBottom: '16px'}}>
                                <strong>Code:</strong> {dataSource.code} | <strong>ID:</strong> {dataSource.id}
                            </div>

                            {editingId === dataSource.id ? (
                                <div style={styles.form}>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Name</label>
                                        <input
                                            style={styles.input}
                                            value={editForm.name || ''}
                                            onChange={(e) => setEditForm(prev => ({...prev, name: e.target.value}))}
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Description</label>
                                        <textarea
                                            style={styles.textarea}
                                            value={editForm.description || ''}
                                            onChange={(e) => setEditForm(prev => ({...prev, description: e.target.value}))}
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>API URL</label>
                                        <input
                                            style={styles.input}
                                            value={editForm.apiUrl || ''}
                                            onChange={(e) => setEditForm(prev => ({...prev, apiUrl: e.target.value}))}
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>API Key</label>
                                        <input
                                            type="password"
                                            style={styles.input}
                                            value={editForm.apiKey || ''}
                                            onChange={(e) => setEditForm(prev => ({...prev, apiKey: e.target.value}))}
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Poll Interval (minutes)</label>
                                        <input
                                            type="number"
                                            style={styles.input}
                                            value={editForm.pollIntervalMinutes || ''}
                                            onChange={(e) => setEditForm(prev => ({...prev, pollIntervalMinutes: parseInt(e.target.value) || undefined}))}
                                        />
                                    </div>
                                    <div style={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={editForm.enabled || false}
                                            onChange={(e) => setEditForm(prev => ({...prev, enabled: e.target.checked}))}
                                        />
                                        <label>Enabled</label>
                                    </div>
                                    <div style={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={editForm.autoImportEnabled || false}
                                            onChange={(e) => setEditForm(prev => ({...prev, autoImportEnabled: e.target.checked}))}
                                        />
                                        <label>Auto Import Enabled</label>
                                    </div>
                                    <div style={styles.formActions}>
                                        <button
                                            style={{...styles.button, ...styles.buttonSecondary}}
                                            onClick={handleCancelEdit}
                                        >
                                            <X size={16} />
                                            Cancel
                                        </button>
                                        <button
                                            style={styles.button}
                                            onClick={() => handleSaveEdit(dataSource.id!)}
                                            disabled={actionLoading === dataSource.id}
                                        >
                                            {actionLoading === dataSource.id ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                            Save
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <p style={{margin: 0, fontSize: '14px', color: 'var(--ink-500)'}}>
                                    {dataSource.description}
                                </p>
                            )}
                        </div>

                        {editingId !== dataSource.id && (
                            <div style={styles.cardContent}>
                                <div style={styles.grid}>
                                    <div style={styles.statCard}>
                                        <div style={styles.statLabel}>API URL</div>
                                        <div style={styles.statValue}>{dataSource.apiUrl || 'Not set'}</div>
                                    </div>
                                    <div style={styles.statCard}>
                                        <div style={styles.statLabel}>Poll Interval</div>
                                        <div style={styles.statValue}>{dataSource.pollIntervalMinutes || 0} minutes</div>
                                    </div>
                                    <div style={styles.statCard}>
                                        <div style={styles.statLabel}>Auto Import</div>
                                        <div style={styles.statValue}>{dataSource.autoImportEnabled ? 'Yes' : 'No'}</div>
                                    </div>
                                    <div style={styles.statCard}>
                                        <div style={styles.statLabel}>Products Imported</div>
                                        <div style={styles.statValue}>{dataSource.totalProductsImported?.toLocaleString() || 0}</div>
                                    </div>
                                    <div style={styles.statCard}>
                                        <div style={styles.statLabel}>Listings Imported</div>
                                        <div style={styles.statValue}>{dataSource.totalListingsImported?.toLocaleString() || 0}</div>
                                    </div>
                                    <div style={styles.statCard}>
                                        <div style={styles.statLabel}>Last Poll</div>
                                        <div style={styles.statValue}>{formatDate(dataSource.lastPollTime)}</div>
                                    </div>
                                    <div style={styles.statCard}>
                                        <div style={styles.statLabel}>Next Scheduled</div>
                                        <div style={styles.statValue}>{formatDate(dataSource.nextScheduledPoll)}</div>
                                    </div>
                                    <div style={styles.statCard}>
                                        <div style={styles.statLabel}>Import Status</div>
                                        <div style={styles.statValue}>{dataSource.lastImportStatus || 'Unknown'}</div>
                                    </div>
                                </div>

                                {dataSource.lastImportError && (
                                    <div style={{
                                        backgroundColor: 'var(--danger-bg)',
                                        border: '1px solid var(--danger-border)',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        marginTop: '16px',
                                    }}>
                                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px'}}>
                                            <AlertCircle size={16} color="var(--danger)" />
                                            <span style={{fontSize: '14px', fontWeight: '500', color: 'var(--danger)'}}>Last Import Error</span>
                                        </div>
                                        <p style={{margin: 0, fontSize: '13px', color: 'var(--danger)', fontFamily: 'monospace'}}>
                                            {dataSource.lastImportError}
                                        </p>
                                    </div>
                                )}

                                {/* Import Controls Section */}
                                <div style={styles.importSection}>
                                    <div style={styles.importActions}>
                                        {isRunning ? (
                                            <button
                                                style={{...styles.button, ...styles.buttonDanger, ...styles.buttonSmall}}
                                                onClick={() => stopImport(dataSource)}
                                                disabled={actionLoading === dataSource.id}
                                            >
                                                {actionLoading === dataSource.id ? <Loader2 size={14} className="animate-spin" /> : <Square size={14} />}
                                                Stop Import
                                            </button>
                                        ) : (
                                            <>
                                                <button
                                                    style={{...styles.button, ...styles.buttonSuccess, ...styles.buttonSmall}}
                                                    onClick={() => startImport(dataSource)}
                                                    disabled={!dataSource.enabled || actionLoading === dataSource.id}
                                                >
                                                    {actionLoading === dataSource.id ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                                                    Start Import
                                                </button>

                                                <div style={{...styles.checkbox, marginLeft: '8px'}}>
                                                    <input
                                                        type="checkbox"
                                                        id={`includeProductDetails-${dataSource.id}`}
                                                        checked={includeProductDetails}
                                                        onChange={(e) => setIncludeProductDetails(e.target.checked)}
                                                        disabled={!dataSource.enabled}
                                                    />
                                                    <label
                                                        htmlFor={`includeProductDetails-${dataSource.id}`}
                                                        style={{fontSize: '12px', color: 'var(--ink-500)', cursor: !dataSource.enabled ? 'not-allowed' : 'pointer'}}
                                                        title="Include drinking windows and critic reviews. Smart enrichment: only fetches details for new or changed vintages. Uncheck only if ProductDetails API is unavailable."
                                                    >
                                                        Include product details
                                                    </label>
                                                </div>
                                            </>
                                        )}

                                        <button
                                            style={{...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall}}
                                            onClick={() => toggleImportHistory(dataSource.id!, dataSource.code!)}
                                        >
                                            <History size={14} />
                                            {isExpanded ? 'Hide History' : 'Show History'}
                                        </button>
                                    </div>

                                    {isExpanded && (
                                        <div>
                                            <h4 style={{margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600'}}>Import History</h4>
                                            {sessions.length > 0 ? (
                                                <table style={styles.table}>
                                                    <thead>
                                                        <tr>
                                                            <th style={styles.th}>Started</th>
                                                            <th style={styles.th}>Duration</th>
                                                            <th style={styles.th}>Status</th>
                                                            <th style={styles.th}>Products</th>
                                                            <th style={styles.th}>Vintages</th>
                                                            <th style={styles.th}>SKUs</th>
                                                            <th style={styles.th}>Listings</th>
                                                            <th style={styles.th}>Gap verify</th>
                                                            <th style={styles.th}>Health</th>
                                                            <th style={styles.th}>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {sessions.map((session) => (
                                                            <tr key={session.id}>
                                                                <td style={styles.td}>
                                                                    {formatDate(session.startTime)}
                                                                </td>
                                                                <td style={styles.td}>
                                                                    {getDuration(session.startTime, session.endTime) || 'N/A'}
                                                                </td>
                                                                <td style={styles.td}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                        {getStatusIcon(session.status)}
                                                                        <span style={{fontSize: '12px'}}>{session.status}</span>
                                                                    </div>
                                                                </td>
                                                                <td style={styles.td}>
                                                                    <EntityCountCell
                                                                        crawlNew={session.newProductsCount || 0}
                                                                        crawlMod={session.modifiedProductsCount || 0}
                                                                        gapNew={session.gapNewProductsCount || 0}
                                                                        gapMod={session.gapModifiedProductsCount || 0}
                                                                    />
                                                                </td>
                                                                <td style={styles.td}>
                                                                    <EntityCountCell
                                                                        crawlNew={session.newVintagesCount || 0}
                                                                        crawlMod={session.modifiedVintagesCount || 0}
                                                                        gapNew={session.gapNewVintagesCount || 0}
                                                                        gapMod={session.gapModifiedVintagesCount || 0}
                                                                    />
                                                                </td>
                                                                <td style={styles.td}>
                                                                    <EntityCountCell
                                                                        crawlNew={session.newSkusCount || 0}
                                                                        crawlMod={session.modifiedSkusCount || 0}
                                                                        gapNew={session.gapNewSkusCount || 0}
                                                                        gapMod={session.gapModifiedSkusCount || 0}
                                                                    />
                                                                </td>
                                                                <td style={styles.td}>
                                                                    <EntityCountCell
                                                                        crawlNew={session.newListingsCount || 0}
                                                                        crawlMod={session.modifiedListingsCount || 0}
                                                                        crawlDel={session.deletedListingsCount || 0}
                                                                        gapNew={session.gapNewListingsCount || 0}
                                                                        gapMod={session.gapModifiedListingsCount || 0}
                                                                        gapDel={session.gapDeletedListingsCount || 0}
                                                                    />
                                                                </td>
                                                                <td style={styles.td}>
                                                                    {(() => {
                                                                        const gapImp = session.gapImportedCount || 0;
                                                                        const gapPhantom = session.gapPhantomCount || 0;
                                                                        const gapEvt = session.gapEventFilteredCount || 0;
                                                                        if (session.status === ImportSessionStatusEnum.Running) {
                                                                            return <span style={{ color: 'var(--ink-500)', fontSize: '11px' }}>—</span>;
                                                                        }
                                                                        if (gapImp === 0 && gapPhantom === 0 && gapEvt === 0) {
                                                                            return <span style={{ color: 'var(--ink-400)', fontSize: '11px' }}>—</span>;
                                                                        }
                                                                        return (
                                                                            <div style={{ fontSize: '11px', lineHeight: '1.3' }} title="gap-fill verification: wines confirmed live this run / stale CSV phantoms skipped / event-like rows filtered. CRUD breakdown is in the entity columns.">
                                                                                <div style={{ color: 'var(--info)' }}>✓{gapImp} verified</div>
                                                                                {gapPhantom > 0 && <div style={{ color: 'var(--ink-400)' }}>phantom: {gapPhantom}</div>}
                                                                                {gapEvt > 0 && <div style={{ color: 'var(--ink-400)' }}>events: {gapEvt}</div>}
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </td>
                                                                <td style={styles.td}>
                                                                    {(() => {
                                                                        const enr = session.enrichmentErrorCount || 0;
                                                                        const proc = session.processingErrorCount || 0;
                                                                        const trunc = session.truncatedSliceCount || 0;
                                                                        const lwinc = session.lwinConflictCount || 0;
                                                                        const srcc = (session as any).sourceConflictCount || 0;
                                                                        const total = enr + proc + trunc + lwinc + srcc;
                                                                        if (session.status === ImportSessionStatusEnum.Running) {
                                                                            return <span style={{ color: 'var(--ink-500)', fontSize: '11px' }}>—</span>;
                                                                        }
                                                                        if (total === 0) {
                                                                            return (
                                                                                <div style={{ color: 'var(--success)', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                                    <CheckCircle size={12} /> OK
                                                                                </div>
                                                                            );
                                                                        }
                                                                        return (
                                                                            <div style={{ fontSize: '11px', lineHeight: '1.3' }} title="enrichment errors / processing errors / truncated slices / LWIN conflicts / cross-source data conflicts (critic scores, drinking windows)">
                                                                                <div style={{ color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}>
                                                                                    <AlertCircle size={12} /> {total} issue{total === 1 ? '' : 's'}
                                                                                </div>
                                                                                {enr > 0 && <div style={{ color: 'var(--danger)' }}>enrich: {enr}</div>}
                                                                                {proc > 0 && <div style={{ color: 'var(--danger)' }}>proc: {proc}</div>}
                                                                                {trunc > 0 && <div style={{ color: 'var(--warning)' }}>trunc: {trunc}</div>}
                                                                                {lwinc > 0 && <div style={{ color: 'var(--warning)' }}>lwin: {lwinc}</div>}
                                                                                {srcc > 0 && <div style={{ color: 'var(--warning)' }}>conflicts: {srcc}</div>}
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </td>
                                                                <td style={styles.td}>
                                                                    {session.status !== ImportSessionStatusEnum.Running && (
                                                                        <button
                                                                            style={{...styles.button, ...styles.buttonDanger, ...styles.buttonSmall, padding: '4px 8px'}}
                                                                            onClick={() => session.id && deleteImportSession(session.id, dataSource.code!)}
                                                                        >
                                                                            <Trash2 size={12} />
                                                                        </button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <p style={{fontSize: '14px', color: 'var(--ink-500)', textAlign: 'center', padding: '20px'}}>
                                                    No import sessions found
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div style={{marginTop: '16px', fontSize: '12px', color: 'var(--ink-400)', display: 'flex', alignItems: 'center', gap: '16px'}}>
                                    <span style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                                        <Calendar size={12} />
                                        Created: {formatDate(dataSource.createdAt)}
                                    </span>
                                    <span style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                                        <Clock size={12} />
                                        Updated: {formatDate(dataSource.updatedAt)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}

            {dataSources.length === 0 && (
                <div style={{textAlign: 'center', padding: '64px', color: 'var(--ink-500)'}}>
                    <p style={{fontSize: '18px', marginBottom: '8px'}}>No data sources found</p>
                    <p style={{fontSize: '14px'}}>Create your first data source to get started</p>
                </div>
            )}
        </div>
    );
};

export default DataSources;

